import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Loader2, MoreHorizontal, Save, Share2 } from "lucide-react";
import { createPortal } from "react-dom";

import { Button } from "@/components/ui/Button";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { FilePicker } from "@/components/ui/FilePicker";
import { Input, Label } from "@/components/ui/Input";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { PageTitleWithHelp, HoverHintTrigger } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { EM_DASH, cn } from "@/lib/utils";
import {
  FLOATING_POPOVER_Z_INDEX,
  computeHorizontalMenuPanel,
  type AnchoredMenuPanelBox,
} from "@/lib/viewportAnchoredPopover";

import {
  FIRMWARE_LIST_KEY,
  FIRMWARE_RELEASE_NOTES_LIST_KEY,
  downloadFirmwareFile,
  fetchFirmwareDetail,
  fetchFirmwareReleaseNoteDetail,
  fetchFirmwareReleaseNotes,
  firmwareDetailKey,
  firmwareReleaseNoteDetailKey,
  firmwareReleaseNoteSelectLabel,
  firmwareReleaseNoteToSelectOption,
  firmwareRecordFileName,
  withFirmwareReleaseNoteSelectOption,
  firmwareWorkflowActionVariant,
  formatFirmwareFixtureFileName,
  transitionFirmware,
  updateFirmware,
  type FirmwareActivity,
  type FirmwareFeature,
  type FirmwarePcbaFamily,
  type FirmwareRecord,
  type FirmwareWorkflowAction,
} from "./firmwareApi";
import { FirmwareAppliesFields, appendFirmwareAppliesFormData } from "./FirmwareAppliesFields";
import { FirmwareReleaseNoteCommentsPanel } from "./FirmwareReleaseNoteCommentsPanel";
import { FirmwareReleaseNoteMarkdown } from "./FirmwareReleaseNoteMarkdown";
import { FirmwareSendToBetaModal } from "./FirmwareSendToBetaModal";
import { FirmwareShareModal } from "./FirmwareShareModal";
import { FirmwareWorkflowStageChips } from "./FirmwareWorkflowStageChips";

const DETAIL_HELP = [
  "This file is identified by its id. Operators refer to it by release notes, family, and feature. The hex file name is assigned automatically.",
  "Use Start testing, Approve, and Put in production to move this file through the firmware workflow.",
].join(" ");

const DOWNLOAD_HOVER_HINT =
  "Download requires Engineering admin access, or a share of this file. Write access is for upload only.";

type FormState = {
  releasedOn: string;
  pcbaFamily: FirmwarePcbaFamily | "";
  feature: FirmwareFeature | "";
  revisionEffective: string;
  revisionEnding: string;
  releaseNoteId: string;
};

export function FirmwareDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const firmwareId = Number.parseInt(params.id ?? "", 10);
  const writesAllowed = useHubModuleWrite("engineering");
  const { showToast } = useHubToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);
  const [baseline, setBaseline] = useState<FormState | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [noteSearch, setNoteSearch] = useState("");
  const [betaModalOpen, setBetaModalOpen] = useState(false);
  const [betaModalError, setBetaModalError] = useState<string | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [overflowMenuOpen, setOverflowMenuOpen] = useState(false);
  const [overflowMenuBox, setOverflowMenuBox] = useState<AnchoredMenuPanelBox | null>(null);
  const overflowMenuButtonRef = useRef<HTMLButtonElement | null>(null);

  const detailQuery = useQuery({
    queryKey: firmwareDetailKey(firmwareId),
    queryFn: () => fetchFirmwareDetail(firmwareId),
    enabled: Number.isFinite(firmwareId) && firmwareId > 0,
    placeholderData: () => {
      const cached = queryClient.getQueriesData<{ firmware: FirmwareRecord[] }>({ queryKey: FIRMWARE_LIST_KEY });
      for (const [, data] of cached) {
        const row = data?.firmware?.find((item) => item.id === firmwareId);
        if (row != null) {
          return { firmware: row, activities: [] as FirmwareActivity[] };
        }
      }
      return undefined;
    },
  });

  useEffect(() => {
    const firmware = detailQuery.data?.firmware;
    if (firmware == null) return;
    const next: FormState = {
      releasedOn: firmware.releasedOn ?? "",
      pcbaFamily: firmware.pcbaFamily ?? "",
      feature: firmware.feature ?? "",
      revisionEffective: firmware.revisionEffective != null ? String(firmware.revisionEffective) : "",
      revisionEnding: firmware.revisionEnding != null ? String(firmware.revisionEnding) : "",
      releaseNoteId: firmware.releaseNoteId != null ? String(firmware.releaseNoteId) : "",
    };
    setForm(next);
    setBaseline(next);
    setFile(null);
  }, [detailQuery.data?.firmware]);

  const notesQuery = useQuery({
    queryKey: [...FIRMWARE_RELEASE_NOTES_LIST_KEY, "picker"],
    queryFn: () => fetchFirmwareReleaseNotes({ limit: 500 }),
  });

  const linkedNoteId = Number.parseInt(form?.releaseNoteId ?? "", 10);
  const linkedNoteQuery = useQuery({
    queryKey: firmwareReleaseNoteDetailKey(linkedNoteId),
    queryFn: () => fetchFirmwareReleaseNoteDetail(linkedNoteId),
    enabled: Number.isFinite(linkedNoteId) && linkedNoteId > 0,
  });

  const selectedNote = useMemo(() => {
    if (form == null || form.releaseNoteId === "") return null;
    const fromList = (notesQuery.data?.releaseNotes ?? []).find(
      (note) => String(note.id) === form.releaseNoteId,
    );
    if (fromList != null) return fromList;
    const fromDetail = linkedNoteQuery.data?.releaseNote;
    if (fromDetail != null && String(fromDetail.id) === form.releaseNoteId) return fromDetail;
    return null;
  }, [form, linkedNoteQuery.data?.releaseNote, notesQuery.data?.releaseNotes]);

  const noteOptions = useMemo(
    () =>
      withFirmwareReleaseNoteSelectOption(
        (notesQuery.data?.releaseNotes ?? []).map(firmwareReleaseNoteToSelectOption),
        selectedNote != null ? firmwareReleaseNoteToSelectOption(selectedNote) : null,
      ),
    [notesQuery.data?.releaseNotes, selectedNote],
  );

  const dirty = useMemo(() => {
    if (form == null || baseline == null) return false;
    return JSON.stringify(form) !== JSON.stringify(baseline) || file != null;
  }, [form, baseline, file]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (form == null) throw new Error("Form is not ready.");
      if (form.releaseNoteId.trim() === "") {
        throw new Error("Link this file to release notes.");
      }
      const body = new FormData();
      appendFirmwareAppliesFormData(body, {
        pcbaFamily: form.pcbaFamily,
        feature: form.feature,
        revisionEffective: form.revisionEffective,
        revisionEnding: form.revisionEnding,
      });
      if (form.releasedOn) body.set("releasedOn", form.releasedOn);
      else body.set("releasedOn", "");
      body.set("releaseNoteId", form.releaseNoteId);
      if (file) body.set("file", file);
      return updateFirmware(firmwareId, body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: firmwareDetailKey(firmwareId) });
      await queryClient.invalidateQueries({ queryKey: FIRMWARE_LIST_KEY });
      showToast({ tone: "success", message: "Firmware saved." });
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  const transitionMutation = useMutation({
    mutationFn: (input: {
      action: FirmwareWorkflowAction;
      betaCustomerFsAccountIds?: number[];
      betaNotes?: string | null;
      betaSerials?: string[];
    }) =>
      transitionFirmware(firmwareId, input.action, {
        betaCustomerFsAccountIds: input.betaCustomerFsAccountIds,
        betaNotes: input.betaNotes,
        betaSerials: input.betaSerials,
      }),
    onSuccess: async (_result, input) => {
      await queryClient.invalidateQueries({ queryKey: firmwareDetailKey(firmwareId) });
      await queryClient.invalidateQueries({ queryKey: FIRMWARE_LIST_KEY });
      setBetaModalOpen(false);
      setBetaModalError(null);
      setOverflowMenuOpen(false);
      showToast({
        tone: "success",
        message:
          input.action === "send_to_beta"
            ? "Beta details saved."
            : input.action === "mark_internal_only"
              ? "Marked internal only."
              : input.action === "clear_internal_only"
                ? "Cleared internal only."
                : "Workflow updated.",
      });
    },
    onError: (error: Error) => {
      if (betaModalOpen) {
        setBetaModalError(error.message);
        return;
      }
      showToast({ tone: "error", message: error.message });
    },
  });

  const downloadMutation = useMutation({
    mutationFn: async () => {
      const firmware = detailQuery.data?.firmware;
      const name =
        firmware != null
          ? firmwareRecordFileName(firmware) || firmware.fileName || `firmware-${firmwareId}.bin`
          : `firmware-${firmwareId}.bin`;
      await downloadFirmwareFile(firmwareId, name);
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  useEffect(() => {
    if (!overflowMenuOpen) {
      setOverflowMenuBox(null);
      return;
    }
    const updatePosition = (): void => {
      const trigger = overflowMenuButtonRef.current;
      if (trigger == null) return;
      setOverflowMenuBox(
        computeHorizontalMenuPanel({
          anchorRect: trigger.getBoundingClientRect(),
          menuWidth: 220,
          horizontalAlign: "end",
          gap: 4,
          maxHeightCap: 320,
        }),
      );
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [overflowMenuOpen]);

  useEffect(() => {
    if (!overflowMenuOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (overflowMenuButtonRef.current?.contains(target)) return;
      setOverflowMenuOpen(false);
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [overflowMenuOpen]);

  if (!Number.isFinite(firmwareId) || firmwareId < 1) {
    return <p className="text-sm text-red-700">Invalid firmware id.</p>;
  }

  if (detailQuery.isLoading || form == null) {
    return (
      <p className="flex items-center gap-2 text-sm text-slate-600">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading firmware…
      </p>
    );
  }

  if (detailQuery.isError || detailQuery.data == null) {
    return <p className="text-sm text-red-700">{(detailQuery.error as Error)?.message ?? "Firmware not found."}</p>;
  }

  const firmware = detailQuery.data.firmware;
  const activities = detailQuery.data.activities;
  const canEdit = writesAllowed && firmware.contentEditable;
  const workflowActions = firmware.allowedActions;
  const overflowActions = firmware.overflowActions ?? [];
  const linkedReleaseNoteId = Number.parseInt(form?.releaseNoteId ?? "", 10);
  const linkedNotesText =
    selectedNote?.notes ??
    (form.releaseNoteId !== "" && form.releaseNoteId === String(firmware.releaseNoteId ?? "")
      ? firmware.releaseNoteNotes
      : null);
  const linkedNotesVersion =
    selectedNote?.versionKey ??
    (form.releaseNoteId !== "" && form.releaseNoteId === String(firmware.releaseNoteId ?? "")
      ? firmware.releaseNoteVersionKey
      : null);

  const predictedFileName =
    linkedNotesVersion != null && form.pcbaFamily !== ""
      ? formatFirmwareFixtureFileName({
          versionKey: linkedNotesVersion,
          pcbaFamily: form.pcbaFamily,
          feature: form.feature === "" ? null : form.feature,
        })
      : "";
  const fileNameDisplay = predictedFileName !== "" ? predictedFileName : firmwareRecordFileName(firmware);
  const titleName = fileNameDisplay !== "" ? fileNameDisplay : `Firmware ${String(firmware.id)}`;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          <PageTitleWithHelp hoverHint={DETAIL_HELP} breadcrumbLabel={titleName}>
            {titleName}
          </PageTitleWithHelp>
          {firmware.approvedByName ? (
            <p className="text-sm text-slate-600">
              Approved by {firmware.approvedByName}
              {firmware.approvedOn ? ` on ${firmware.approvedOn}` : ""}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {writesAllowed ? (
            <Button variant="secondary" onClick={() => setShareModalOpen(true)}>
              <Share2 className="mr-2 h-4 w-4" />
              Share
            </Button>
          ) : null}
          {firmware.hasFile && firmware.canDownload ? (
            <Button
              variant="secondary"
              onClick={() => navigate(`/engineering/firmware/${String(firmwareId)}/analyzer`)}
            >
              Logic analyzer
            </Button>
          ) : null}
          {firmware.hasFile ? (
            <HoverHintTrigger trigger={
              <Button
                variant="secondary"
                disabled={!firmware.canDownload || downloadMutation.isPending}
                onClick={() => downloadMutation.mutate()}
              >
                {downloadMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Download
              </Button>
            }>
              {DOWNLOAD_HOVER_HINT}
            </HoverHintTrigger>
          ) : null}
          {writesAllowed && overflowActions.length > 0 ? (
            <Button
              ref={overflowMenuButtonRef}
              type="button"
              variant="secondary"
              disabled={transitionMutation.isPending}
              aria-label="More firmware actions"
              aria-haspopup="menu"
              aria-expanded={overflowMenuOpen}
              onClick={() => setOverflowMenuOpen((open) => !open)}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          ) : null}
          {writesAllowed
            ? workflowActions.map((action) => (
                <Button
                  key={`${action.action}-${action.to}`}
                  variant={firmwareWorkflowActionVariant(action.action)}
                  disabled={transitionMutation.isPending}
                  onClick={() => {
                    transitionMutation.mutate({ action: action.action });
                  }}
                >
                  {transitionMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {action.label}
                </Button>
              ))
            : null}
          {canEdit ? (
            <HubSaveButton dirty={dirty} isLoading={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
              {saveMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save
            </HubSaveButton>
          ) : null}
        </div>
      </header>
      <FirmwareWorkflowStageChips
        current={firmware.workflowState}
        sentToBeta={firmware.sentToBeta}
        betaCustomers={firmware.betaCustomers ?? []}
        internalOnly={firmware.internalOnly}
      />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)]">
        <Card>
          <CardHeader>
            <CardTitle>Release details</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4">
            <div>
              <Label htmlFor="detail-released">Released</Label>
              <Input
                id="detail-released"
                type="date"
                className="mt-1.5"
                value={form.releasedOn}
                disabled={!canEdit}
                onChange={(event) => setForm({ ...form, releasedOn: event.target.value })}
              />
            </div>
            <FirmwareAppliesFields
              disabled={!canEdit}
              value={{
                pcbaFamily: form.pcbaFamily,
                feature: form.feature,
                revisionEffective: form.revisionEffective,
                revisionEnding: form.revisionEnding,
              }}
              onChange={(next) => setForm({ ...form, ...next })}
            />
            <div>
              <Label htmlFor="detail-note">Release notes</Label>
              {canEdit ? (
                <div className="mt-1.5 space-y-2">
                  <SearchableSelect
                    id="detail-note"
                    value={form.releaseNoteId}
                    onValueChange={(option) => setForm({ ...form, releaseNoteId: option?.value ?? "" })}
                    options={noteOptions}
                    searchValue={noteSearch}
                    onSearchChange={setNoteSearch}
                    placeholder="Link a shared changelog"
                    searchPlaceholder="Search version"
                    emptyMessage="No release notes match."
                    isLoading={notesQuery.isLoading && selectedNote == null}
                    selectedLabelOverride={
                      selectedNote != null
                        ? firmwareReleaseNoteSelectLabel(selectedNote)
                        : form.releaseNoteId === String(firmware.releaseNoteId ?? "")
                          ? firmwareReleaseNoteSelectLabel({
                              versionKey: firmware.releaseNoteVersionKey,
                              productLine: firmware.releaseNoteProductLine,
                            }) || null
                          : null
                    }
                  />
                  {linkedNotesVersion != null && form.releaseNoteId !== "" ? (
                    <p className="text-sm">
                      <Link
                        className="text-sky-700 hover:underline"
                        to={`/engineering/firmware?tab=notes&ids=${form.releaseNoteId}`}
                      >
                        Open {linkedNotesVersion} release notes
                      </Link>
                    </p>
                  ) : null}
                </div>
              ) : linkedNotesVersion != null ? (
                <p className="mt-1.5 text-sm">
                  <Link
                    className="text-sky-700 hover:underline"
                    to={`/engineering/firmware?tab=notes&ids=${form.releaseNoteId}`}
                  >
                    {linkedNotesVersion}
                  </Link>
                </p>
              ) : (
                <p className="mt-1.5 text-sm text-slate-600">No shared release notes linked.</p>
              )}
              {form.releaseNoteId !== "" && linkedNotesText ? (
                <div className="mt-3 max-h-80 overflow-auto rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                  <FirmwareReleaseNoteMarkdown text={linkedNotesText} />
                </div>
              ) : form.releaseNoteId !== "" ? (
                <p className="mt-3 text-sm text-slate-600">Loading linked notes…</p>
              ) : null}
              {Number.isFinite(linkedReleaseNoteId) && linkedReleaseNoteId > 0 ? (
                <FirmwareReleaseNoteCommentsPanel releaseNoteId={linkedReleaseNoteId} writesAllowed={false} />
              ) : null}
            </div>
            {firmware.sentToBeta ||
            (firmware.betaCustomers?.length ?? 0) > 0 ||
            (firmware.betaSerials?.length ?? 0) > 0 ||
            (firmware.betaNotes != null && firmware.betaNotes !== "") ? (
              <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Beta customer</p>
                {(firmware.betaCustomers ?? []).length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {(firmware.betaCustomers ?? []).map((customer) => (
                      <span
                        key={customer.fsAccountId}
                        className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-800"
                      >
                        {customer.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-600">Sent to beta, with no customers recorded.</p>
                )}
                {firmware.betaNotes ? (
                  <p className="whitespace-pre-wrap text-sm text-slate-700">{firmware.betaNotes}</p>
                ) : null}
                {(firmware.betaSerials ?? []).length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {(firmware.betaSerials ?? []).map((serial) => (
                      <span
                        key={serial}
                        className="rounded-md border border-slate-200 bg-white px-2 py-0.5 font-mono text-xs text-slate-800"
                      >
                        {serial}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
            <div>
              <Label>File name</Label>
              <p className="mt-1.5 text-sm text-slate-800">
                {fileNameDisplay !== "" ? fileNameDisplay : "Upload a hex to assign a file name."}
              </p>
            </div>
            {canEdit ? (
              <FilePicker
                id="detail-file"
                label="Replace file"
                accept=".hex,.zip,application/zip,application/octet-stream"
                hoverHint="Upload the .hex, or a zip that contains one .hex. The hub stores the hex and names it from software version, family, and feature."
                onChange={(next) => setFile(next)}
              />
            ) : null}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Activity</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            {activities.length === 0 ? (
              <p className="text-sm text-slate-600">No activity yet.</p>
            ) : (
              activities.map((activity) => (
                <div key={activity.id} className="border-b border-slate-100 pb-2 text-sm last:border-0">
                  <div className="font-medium text-slate-800">{activity.actionType}</div>
                  <div className="text-xs text-slate-500">
                    {activity.actorEmail} · {new Date(activity.activityAt).toLocaleString()}
                  </div>
                  {activity.fieldName ? (
                    <div className="mt-1 text-slate-600">
                      {activity.fieldName}: {activity.oldValue ?? EM_DASH} → {activity.newValue ?? EM_DASH}
                    </div>
                  ) : null}
                  {activity.note ? <div className="mt-1 text-slate-600">{activity.note}</div> : null}
                </div>
              ))
            )}
          </CardBody>
        </Card>
      </div>
      {overflowMenuOpen && overflowMenuBox != null
        ? createPortal(
            <div
              className="fixed overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
              style={{
                left: overflowMenuBox.left,
                top: overflowMenuBox.top,
                width: overflowMenuBox.width,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
              role="menu"
              onPointerDown={(event) => event.stopPropagation()}
            >
              {overflowActions.map((action) => (
                <button
                  key={action.action}
                  type="button"
                  role="menuitem"
                  className={cn(
                    "block w-full px-3 py-2 text-left text-sm hover:bg-slate-50",
                    action.action === "reject" ? "text-red-700" : "text-slate-800",
                  )}
                  disabled={transitionMutation.isPending}
                  onClick={() => {
                    if (action.action === "send_to_beta") {
                      setBetaModalError(null);
                      setBetaModalOpen(true);
                      setOverflowMenuOpen(false);
                      return;
                    }
                    transitionMutation.mutate({ action: action.action });
                  }}
                >
                  {action.label}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
      <FirmwareSendToBetaModal
        open={betaModalOpen}
        pending={transitionMutation.isPending}
        errorMessage={betaModalError}
        initialCustomers={firmware.betaCustomers ?? []}
        initialNotes={firmware.betaNotes ?? ""}
        initialSerials={firmware.betaSerials ?? []}
        onClose={() => {
          if (transitionMutation.isPending) {
            return;
          }
          setBetaModalOpen(false);
          setBetaModalError(null);
        }}
        onConfirm={(input) => {
          setBetaModalError(null);
          transitionMutation.mutate({
            action: "send_to_beta",
            betaCustomerFsAccountIds: input.customers.map((customer) => customer.fsAccountId),
            betaNotes: input.notes,
            betaSerials: input.serials,
          });
        }}
      />
      <FirmwareShareModal
        open={shareModalOpen}
        firmwareId={firmwareId}
        fileLabel={titleName}
        onClose={() => setShareModalOpen(false)}
      />
    </div>
  );
}
